// Fonte ÚNICA de verdade das colunas da extração.
// Constrói as MATRIZES (cabeçalho + linhas + metadados de tipo/banda) que tanto a
// exportação Excel (`excelExport.ts`) quanto a planilha na tela (`SpreadsheetGrid`)
// consomem — assim a tela e o .xlsx nunca divergem. Este módulo é puro: sem React,
// sem xlsx. Recortado de excelExport.ts (a lógica de conversão/mapeamento não muda).
import type {
  FaturaEstruturada, ReconciliacaoResultado, ExtItemFatura,
  AbaPlanilhaExtracao, CorrecaoCelulaPlanilha, CorrecoesPlanilha,
} from '../../../../types';

export type ColType = 'txt' | 'num' | 'date' | 'dec6';
export interface ColMeta { label: string; type: ColType; band?: 'yellow' | 'red'; }

// As abas e o formato da camada de correção são tipos de DOMÍNIO (vivem no banco):
// a fonte é `types.ts`, aqui só se reexporta para quem já importa deste módulo.
export type TabKey = AbaPlanilhaExtracao;
export type { CorrecaoCelulaPlanilha as CorrecaoCelula, CorrecoesPlanilha };
export const TAB_KEYS: TabKey[] = ['faturas', 'itens', 'medidor', 'auditoria', 'erros'];

/** De qual fatura (e de qual sub-registro dentro dela) veio a linha do grid. É esta
 *  âncora — e não o número da linha, que muda com o filtro de status — que amarra a
 *  correção salva à célula certa.
 *  `chave` identifica o sub-registro pelo CONTEÚDO (descrição do item, nº do medidor,
 *  campo da checagem), nunca pela posição: reprocessar a fatura e o parser devolver os
 *  itens em outra ordem não pode fazer a correção cair na linha do vizinho. Na aba
 *  Faturas é sempre '0' — lá é uma linha por fatura e não há o que confundir. */
export interface RowOwner { fatura: number; chave: string; }

/** Uma aba já pronta para renderizar/exportar. `rows` é a matriz CRUA (datas = serial
 *  Excel, números = number) — idêntica à que o Excel usa. `highlightCol`: coluna cujas
 *  células NÃO vazias ganham fundo âmbar (motivo da revisão) na tela e no .xlsx.
 *  `corrigidas`: chaves "r:c" já gravadas no banco (camada de correção manual). */
export interface GridModel {
  columns: ColMeta[];
  groupHeaders?: string[];
  rows: (string | number)[][];
  severityCol?: number;
  highlightCol?: number;
  rowOwner?: RowOwner[];
  corrigidas?: Set<string>;
}

// v2: a âncora do sub-registro passou a ser o conteúdo (ver RowOwner.chave); a v1
// usava o índice da linha e não sobrevivia a um reprocessamento que reordenasse itens.
export const VERSAO_CORRECOES = 2;

export interface ExcelFaturaItem {
  arquivo: string;
  estruturado: FaturaEstruturada;
  reconciliacao: ReconciliacaoResultado | null;
  /** Origem do PDF original (p/ o ZIP): bytes em memória e/ou caminho no Storage. */
  pdfFile?: File;
  pdfPath?: string | null;
  /** `extracoes_faturas.id` — sem ele a planilha exibe mas não tem onde salvar. */
  extracaoId?: string;
  /** Correções manuais já gravadas no banco, aplicadas por cima do que o robô extraiu. */
  correcoes?: CorrecoesPlanilha | null;
}
export interface ExcelErroItem { arquivo: string; erro: string; }

// ─────────────────────── helpers de conversão (do template antigo) ───────────────────────
// Número BR robusto a ruído de OCR (espelha parseBrNumber do parser): trata
// "1.162.438,21", "284,47", "0.891810" e o artefato "-1.986.78".
export const parseBrNum = (val: unknown): number | string => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'number') return val;
  let s = String(val).trim().replace(/%/g, '').replace(/\s+/g, '');
  if (s === '' || s === '-') return '';
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  s = s.replace(/[^\d.,]/g, '');
  if (s === '') return '';
  const hasDot = s.includes('.'), hasComma = s.includes(',');
  let norm: string;
  if (hasComma) norm = s.replace(/\./g, '').replace(',', '.');
  else if (hasDot) {
    const parts = s.split('.');
    const last = parts[parts.length - 1];
    if (parts.length > 2) norm = last.length <= 2 ? parts.slice(0, -1).join('') + '.' + last : parts.join('');
    else if (last.length === 3 && parts[0].length >= 1 && parts[0].length <= 3) norm = parts.join('');
    else norm = s;
  } else norm = s;
  const num = parseFloat(norm);
  return isNaN(num) ? (typeof val === 'string' ? val : '') : (neg ? -num : num);
};

export const dateToSerial = (val: string): number | string => {
  if (!val) return '';
  let y: number, m: number, d: number;
  const iso = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3]; }
  else {
    const br = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) { d = +br[1]; m = +br[2]; y = +br[3]; } else return val;
  }
  const date = new Date(y, m - 1, d);
  const epoch = new Date(1899, 11, 30);
  return Math.round((date.getTime() - epoch.getTime()) / 86400000);
};

const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const contaMesToStr = (val: string): string => {
  if (!val) return '';
  const iso = val.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (iso) return `${mesesAbrev[+iso[2] - 1]}/${iso[1].slice(2)}`;
  const num = val.match(/^(\d{2})\/(\d{4})$/);
  if (num) return `${mesesAbrev[+num[1] - 1]}/${num[2].slice(2)}`;
  const txt = val.match(/^([A-Za-z]{3})\/(\d{4})$/i);
  if (txt) return `${txt[1].toLowerCase()}/${txt[2].slice(2)}`;
  return val;
};

/** Inverso de `contaMesToStr`: 'set/26' | '09/2026' | '2026-09' → '2026-09'.
 *  Usado ao salvar a correção de Mês/Ref no escalar `mes_referencia` (que o
 *  Histórico filtra como 'YYYY-MM'). Devolve '' quando não reconhece o formato. */
export const contaMesToIso = (val: string): string => {
  const s = String(val || '').trim().toLowerCase();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const num = s.match(/^(\d{1,2})\/(\d{2,4})$/);
  if (num) {
    const mes = +num[1];
    const ano = num[2].length === 2 ? 2000 + +num[2] : +num[2];
    if (mes >= 1 && mes <= 12) return `${ano}-${String(mes).padStart(2, '0')}`;
    return '';
  }
  const txt = s.match(/^([a-zç]{3})[a-zç]*\/(\d{2,4})$/);
  if (txt) {
    const idx = mesesAbrev.indexOf(txt[1]);
    if (idx === -1) return '';
    const ano = txt[2].length === 2 ? 2000 + +txt[2] : +txt[2];
    return `${ano}-${String(idx + 1).padStart(2, '0')}`;
  }
  return '';
};

const formatCep = (val: string): string => {
  if (!val) return '';
  const digits = String(val).replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 2)}.${digits.slice(2, 5)}-${digits.slice(5)}` : val;
};

const extractPercentage = (val: unknown): string => {
  if (!val) return '';
  const str = String(val);
  const m = str.match(/(\d+[,.]\d+%?)\s*$/);
  if (m) return m[1].includes('%') ? m[1] : m[1] + '%';
  if (str.includes('=')) return str.split('=').pop()?.trim() || '';
  return str;
};

const aliqStr = (val: string): string => {
  if (!val) return '';
  const s = String(val).trim();
  return s.endsWith('%') ? s : s + '%';
};

const normalizeStr = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Z0-9]/gi, '').toUpperCase();

const getSafeTarifa = (it: any) => {
  const t = parseFloat(String(it?.tarifa_unit).replace(',', '.')) || 0;
  const p = parseFloat(String(it?.preco_unit_com_tributos).replace(',', '.')) || 0;
  if (t > 0 && p > 0 && t > p) return it.preco_unit_com_tributos;
  return it?.tarifa_unit || '';
};
const getSafePreco = (it: any) => {
  const t = parseFloat(String(it?.tarifa_unit).replace(',', '.')) || 0;
  const p = parseFloat(String(it?.preco_unit_com_tributos).replace(',', '.')) || 0;
  if (t > 0 && p > 0 && t > p) return it.tarifa_unit;
  return it?.preco_unit_com_tributos || '';
};

const mediaHistorico = (consumos: number[]): number | '' => {
  const vals = consumos.filter(v => typeof v === 'number' && !isNaN(v));
  if (!vals.length) return '';
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
};

// ─────────────────────── cabeçalho do template (linha 1) ───────────────────────
export const BASE_HEADERS = [
  'Unidade Consumidora', 'Nome/Razão Social', 'CPF/CNPJ', 'Numero Parceiro ', 'Data de Nascimento ', 'Mês/Ref',
  'Data da Leitura Anterior', 'Data da Leitura Atual', 'N° de Dias', 'Próxima Leitura', 'Apresentação', 'Vencimento',
  'Grupo', 'Sub Grupo', 'Classificação', 'Tipo de Fornecimento', 'Leitura anterior', 'Leitura atual', 'Constant Medidor',
  'Consumo (kWh)', 'PIS/PASEP', 'ICMS', 'COFINS', 'Endereço', 'Bairro', 'CEP', 'Cidade',
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez', 'Média',
  'UC Geradora', 'GERAÇÃO CICLO  KWH', 'CICLO (MÊS)', 'EXCEDENTE RECEBIDO KWH',
  'UC Geradora', 'GERAÇÃO CICLO  KWH', 'CICLO (MÊS)', 'EXCEDENTE RECEBIDO KWH',
  'CRÉDITO RECEBIDO', 'SALDO', 'CADASTRO RATEIO', 'UFER',
  'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'CONSUMO FP SCEE', 'CONSUMO HI SCEE', 'CONSUMO P SCEE', 'CONSUMO HR SCEE', 'CONSUMO NÃO COMPENSADO',
  'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'ADC BANDEIRA', 'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'CONSUMO SCEE', 'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'UC', 'Tipo', 'INJEÇÃO SCEE', 'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'UC', 'Tipo', 'INJEÇÃO SCEE', 'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'DEMANDA - kW', 'DEMANDA', 'Qtd', 'Tarifa unit.(R$) - DEMANDA', 'Preço Unit (R$) com Tributos Demanda', 'Valor(R$) -DEMANDA',
  'DEMANDA ISENTO DE ICMS', 'Qtd', 'Tarifa unit.(R$) - DEMANDA', 'Preço Unit (R$) com Tributos Demanda Isento de ICMS', 'Valor(R$) -DEMANDA ISENTO DE ICMS',
  'CONSUMO SCEE FP - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) - FP TUSD',
  'INJEÇÃO SCEE FP - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) - TUSD',
  'CONSUMO SCEE FP - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) - FP TE',
  'INJEÇÃO SCEE FP - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) - TE',
  'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'INJEÇÃO SCEE HI', 'Tarifa unit.(R$)', 'Preço Unit (R$) com Tributos', 'Valor(R$)',
  'CONSUMO SCEE P - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) -  TUSD',
  'INJEÇÃO SCEE P - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) - TUSD',
  'CONSUMO SCEE P - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) -  TE',
  'INJEÇÃO SCEE P - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) - TE',
  'CONSUMO SCEE HR - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) - TUSD',
  'INJEÇÃO SCEE HR - GDI - TUSD', 'Tarifa unit.(R$) - TUSD', 'Preço Unit (R$) com Tributos - TUSD', 'Valor(R$) - TUSD',
  'CONSUMO SCEE HR - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) - TE',
  'INJEÇÃO SCEE HR - GDI - TE', 'Tarifa unit.(R$) - TE', 'Preço Unit (R$) com Tributos - TE', 'Valor(R$) - TE',
  'ENERGIA COMP NÃO ISENTA', 'ENERGIA COMP NÃO ISENTA', 'PARC INJET S/DESC', 'Quantidade', 'Preço Unit (R$) com Tributos',
  'CONTRIB. ILUM. PÚBLICA - MUNICIPAL', 'JUROS MORATÓRIA', 'MULTA', 'Valor do Boleto', 'Codigo de Barras', 'Código Pix',
  'Nº do Medidor', 'Débito Automático',
];

// Remove colunas por índice (dropa Numero Parceiro/Data de Nascimento — nunca vêm na fatura).
function dropColumns<T>(arr: T[], drop: number[]): T[] {
  const d = new Set(drop);
  return arr.filter((_, i) => !d.has(i));
}
// Remapeia um conjunto de índices de coluna após remover `drop`.
function remapIndexSet(set: Set<number>, drop: number[]): Set<number> {
  const sorted = [...drop].sort((a, b) => a - b);
  const out = new Set<number>();
  for (const c of set) {
    if (sorted.includes(c)) continue;
    out.add(c - sorted.filter((d) => d < c).length);
  }
  return out;
}

// ─────────────────────── motivo de revisão (coluna 1 da aba Faturas) ───────────────────────
/** Resume POR QUE a fatura não está "Confere": críticos primeiro, campos ausentes
 *  agrupados numa frase só. Vazio quando o status é ok. */
export function motivoRevisao(rec: ReconciliacaoResultado | null): string {
  if (!rec) return 'Sem reconciliação salva.';
  if (rec.status === 'ok') return '';
  const probs = (rec.checagens || []).filter(c => c.tipo === 'critico' || c.tipo === 'alerta');
  const camposFaltando = probs
    .filter(c => c.categoria === 'Campos' && /não foi extraíd/i.test(c.mensagem))
    .map(c => c.campo);
  const outras = probs.filter(c => !(c.categoria === 'Campos' && /não foi extraíd/i.test(c.mensagem)));
  outras.sort((a, b) => (a.tipo === b.tipo ? 0 : a.tipo === 'critico' ? -1 : 1));
  const msgs = outras.map(c => c.mensagem);
  if (camposFaltando.length) msgs.push(`Não extraído(s): ${camposFaltando.join(', ')}.`);
  const MAX = 3;
  const head = msgs.slice(0, MAX).join(' • ');
  return msgs.length > MAX ? `${head} • (+${msgs.length - MAX} problema(s))` : head;
}

// ─────────────────────── monta UMA linha da aba Faturas ───────────────────────
function buildFaturaRow(f: ExcelFaturaItem, nCols: number): (string | number)[] {
  const est = f.estruturado || ({} as FaturaEstruturada);
  const cab: any = est.cabecalho || {};
  const itens: ExtItemFatura[] = est.itens || [];
  const scee = est.scee || [];
  const saldos: any = est.saldos || {};
  const historico = est.historico || [];

  const findVal = (desc: string, exact = false, excludes: string[] = []): any => {
    const nd = normalizeStr(desc);
    return itens.find((i) => {
      const ds = normalizeStr(i.descricao || '');
      if (exact) return ds === nd;
      if (!ds.includes(nd)) return false;
      if (excludes.some(ex => ds.includes(normalizeStr(ex)))) return false;
      return true;
    }) || {};
  };
  const findConsumoGeral = (): any => {
    const ex = ['SCEE', 'COMPENSADO', 'TUSD', 'TE', 'INJECAO', 'BANDEIRA'];
    return itens.find((i) => {
      const d = normalizeStr(i.descricao || '');
      if (!d.includes('CONSUMO') && !d.includes('ENERGIAATIVA') && !d.includes('FORNECIMENTO')) return false;
      return !ex.some(t => d.includes(normalizeStr(t)));
    }) || {};
  };

  const row: (string | number)[] = new Array(nCols).fill('');

  let grupo = cab.grupo || '';
  let subGrupo = cab.sub_grupo || '';
  let classificacao = cab.classificacao || '';
  if (!grupo && classificacao) {
    const cm = classificacao.match(/^([AB])\s*([AB]?\d?)\s*(.*)/i);
    if (cm) {
      grupo = cm[1].toUpperCase();
      const rawSub = cm[2] ? cm[2].trim() : '';
      if (rawSub) subGrupo = /^[AB]/i.test(rawSub) ? rawSub.toUpperCase() : (grupo + rawSub).toUpperCase();
      classificacao = cm[3] ? cm[3].replace(/^[-\s]+/, '').trim() : '';
    }
  }

  row[0] = cab.unidade_consumidora || '';
  row[1] = cab.nome_cliente || '';
  row[2] = cab.cpf_cnpj || '';
  row[3] = cab.numero_parceiro || '';
  row[4] = cab.data_nascimento || '';
  row[5] = contaMesToStr(cab.conta_mes || '');
  row[6] = dateToSerial(cab.data_leitura_anterior || cab.leitura_anterior_medidor || '');
  row[7] = dateToSerial(cab.data_leitura_atual || cab.leitura_atual_medidor || '');
  row[8] = parseBrNum(cab.n_dias);
  row[9] = dateToSerial(cab.proxima_leitura || '');
  row[10] = dateToSerial(cab.apresentacao || '');
  row[11] = dateToSerial(cab.vencimento || '');
  row[12] = grupo;
  row[13] = subGrupo;
  row[14] = classificacao;
  row[15] = cab.tipo_fornecimento || '';
  row[16] = parseBrNum(cab.leitura_anterior_medidor);
  row[17] = parseBrNum(cab.leitura_atual_medidor);
  row[18] = parseBrNum(cab.constant_medidor);
  row[19] = parseBrNum(cab.consumo_kwh);
  const trib = (tipo: string) => (est.tributos || []).find(t => t.tipo === tipo) || ({} as any);
  row[20] = aliqStr(trib('PIS_PASEP').aliquota);
  row[21] = aliqStr(trib('ICMS').aliquota);
  row[22] = aliqStr(trib('COFINS').aliquota);
  row[23] = cab.end_logradouro || '';
  row[24] = cab.end_bairro || '';
  row[25] = formatCep(cab.end_cep || '');
  row[26] = cab.end_cidade || '';

  const mesesMap = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const deduped = new Map<string, { consumo: string; year: number }>();
  for (const m of historico) {
    const parts = (m.mes_ano || '').split('/');
    if (parts.length < 2) continue;
    const monthName = parts[0].toUpperCase();
    let yr = parseInt(parts[1]); if (yr < 100) yr += 2000;
    const ex = deduped.get(monthName);
    if (!ex || yr > ex.year) deduped.set(monthName, { consumo: m.consumo_faturado, year: yr });
  }
  const sortedHist = [...deduped.entries()].map(([month, dat]) => ({ month, ...dat }))
    .sort((a, b) => b.year !== a.year ? b.year - a.year : mesesMap.indexOf(b.month) - mesesMap.indexOf(a.month))
    .slice(0, 12);
  const consumosNum: number[] = [];
  for (const { month, consumo } of sortedHist) {
    const idx = mesesMap.indexOf(month);
    if (idx !== -1) { const n = parseBrNum(consumo); row[27 + idx] = n; if (typeof n === 'number') consumosNum.push(n); }
  }
  row[39] = mediaHistorico(consumosNum);

  if (scee[0]) { row[40] = scee[0].uc_geradora || ''; row[41] = parseBrNum(scee[0].geracao_ciclo_kwh); row[42] = scee[0].ciclo_mes || ''; row[43] = parseBrNum(scee[0].excedente_recebido_kwh); }
  if (scee[1]) { row[44] = scee[1].uc_geradora || ''; row[45] = parseBrNum(scee[1].geracao_ciclo_kwh); row[46] = scee[1].ciclo_mes || ''; row[47] = parseBrNum(scee[1].excedente_recebido_kwh); }

  row[48] = parseBrNum(saldos.credito_recebido);
  row[49] = parseBrNum(saldos.saldo);
  row[50] = extractPercentage(scee[0]?.percent_rateio || saldos.cadastro_rateio);
  const uferItens = itens.filter((i) => normalizeStr(i.descricao).includes('UFER'));
  row[51] = uferItens.length
    ? (uferItens.reduce((s, i) => s + (Number(parseBrNum(i.valor)) || 0), 0) || '')
    : parseBrNum(saldos.ufer);

  const consumoGeral = findConsumoGeral();
  row[52] = parseBrNum(getSafeTarifa(consumoGeral));
  row[53] = parseBrNum(getSafePreco(consumoGeral));
  row[54] = parseBrNum(consumoGeral.valor);

  row[55] = parseBrNum(findVal('CONSUMO FP SCEE', false, ['TUSD', 'TE'])?.qtd);
  row[56] = parseBrNum(findVal('CONSUMO HI SCEE', false, ['TUSD', 'TE'])?.qtd);
  row[57] = parseBrNum(findVal('CONSUMO P SCEE', false, ['TUSD', 'TE'])?.qtd);
  row[58] = parseBrNum(findVal('CONSUMO HR SCEE', false, ['TUSD', 'TE'])?.qtd);

  const naoComp = findVal('COMPENSADO', false, ['INJECAO', 'TUSD', 'TE']);
  row[59] = parseBrNum(naoComp.qtd);
  row[60] = parseBrNum(getSafeTarifa(naoComp));
  row[61] = parseBrNum(getSafePreco(naoComp));
  row[62] = parseBrNum(naoComp.valor);

  const band = findVal('BANDEIRA');
  row[63] = parseBrNum(band.qtd);
  row[64] = parseBrNum(getSafeTarifa(band));
  row[65] = parseBrNum(getSafePreco(band));
  row[66] = parseBrNum(band.valor);

  const cScee = findVal('CONSUMO SCEE', false, ['FP', 'HI', 'HR', 'P ', 'P-', 'TUSD', 'TE', 'INJECAO', 'COMPENSADO']);
  row[67] = parseBrNum(cScee.qtd);
  row[68] = parseBrNum(getSafeTarifa(cScee));
  row[69] = parseBrNum(getSafePreco(cScee));
  row[70] = parseBrNum(cScee.valor);

  const injecoes = itens.filter((i) => {
    const d = normalizeStr(i.descricao || '');
    return d.includes('INJECAOSCEE') && !d.includes('TUSD') && !d.includes('TE') && !d.includes('FP') && !d.includes('HI') && !d.includes('HR');
  });
  if (injecoes[0]) { row[71] = injecoes[0].uc_geradora || ''; row[72] = injecoes[0].tipo_gd || ''; row[73] = parseBrNum(injecoes[0].qtd); row[74] = parseBrNum(getSafeTarifa(injecoes[0])); row[75] = parseBrNum(getSafePreco(injecoes[0])); row[76] = parseBrNum(injecoes[0].valor); }
  if (injecoes[1]) { row[77] = injecoes[1].uc_geradora || ''; row[78] = injecoes[1].tipo_gd || ''; row[79] = parseBrNum(injecoes[1].qtd); row[80] = parseBrNum(getSafeTarifa(injecoes[1])); row[81] = parseBrNum(getSafePreco(injecoes[1])); row[82] = parseBrNum(injecoes[1].valor); }

  const demanda = findVal('DEMANDA', false, ['ISENTO']);
  row[83] = parseBrNum(demanda.qtd);
  row[84] = demanda.valor ? 'DEMANDA' : '';
  row[85] = parseBrNum(demanda.qtd);
  row[86] = parseBrNum(getSafeTarifa(demanda));
  row[87] = parseBrNum(getSafePreco(demanda));
  row[88] = parseBrNum(demanda.valor);

  const demIsento = findVal('DEMANDA ISENTO');
  row[89] = demIsento.valor ? 'SIM' : '';
  row[90] = parseBrNum(demIsento.qtd);
  row[91] = parseBrNum(getSafeTarifa(demIsento));
  row[92] = parseBrNum(getSafePreco(demIsento));
  row[93] = parseBrNum(demIsento.valor);

  const setTusdTe = (desc: string, startIdx: number) => {
    const it = findVal(desc);
    row[startIdx] = parseBrNum(it?.qtd);
    row[startIdx + 1] = parseBrNum(getSafeTarifa(it));
    row[startIdx + 2] = parseBrNum(getSafePreco(it));
    row[startIdx + 3] = parseBrNum(it?.valor);
  };
  setTusdTe('CONSUMO SCEE FP TUSD', 94);
  setTusdTe('INJECAO SCEE FP TUSD', 98);
  setTusdTe('CONSUMO SCEE FP TE', 102);
  setTusdTe('INJECAO SCEE FP TE', 106);
  setTusdTe('INJECAO SCEE HI', 113);
  setTusdTe('CONSUMO SCEE P TUSD', 117);
  setTusdTe('INJECAO SCEE P TUSD', 121);
  setTusdTe('CONSUMO SCEE P TE', 125);
  setTusdTe('INJECAO SCEE P TE', 129);
  setTusdTe('CONSUMO SCEE HR TUSD', 133);
  setTusdTe('INJECAO SCEE HR TUSD', 137);
  setTusdTe('CONSUMO SCEE HR TE', 141);
  setTusdTe('INJECAO SCEE HR TE', 145);

  const energiaComp = itens.filter((i) => normalizeStr(i.descricao).includes('COMPNAOISENTA'));
  row[149] = parseBrNum(energiaComp[0]?.valor || energiaComp[0]?.qtd);
  row[150] = parseBrNum(energiaComp[1]?.valor || energiaComp[1]?.qtd);

  const parcInjet = findVal('PARC INJET');
  row[151] = parcInjet?.descricao || '';
  row[152] = parseBrNum(parcInjet?.qtd);
  row[153] = parseBrNum(parcInjet?.preco_unit_com_tributos);

  const allIlum = itens.filter((i) => { const d = normalizeStr(i.descricao || ''); return (d.includes('ILUMPUBLICA') || d.includes('COSIP')) && !d.includes('JUROS'); });
  row[154] = allIlum.reduce((s, i) => s + (Number(parseBrNum(i.valor)) || 0), 0) || '';
  const allJuros = itens.filter((i) => normalizeStr(i.descricao || '').includes('JURO'));
  row[155] = allJuros.reduce((s, i) => s + (Number(parseBrNum(i.valor)) || 0), 0) || '';
  const allMultas = itens.filter((i) => normalizeStr(i.descricao || '').includes('MULTA'));
  row[156] = allMultas.reduce((s, i) => s + (Number(parseBrNum(i.valor)) || 0), 0) || '';
  row[157] = parseBrNum(cab.total_pagar);
  row[158] = cab.codigo_barras || '';
  row[159] = cab.pix_copia_cola || '';
  row[160] = cab.numero_medidor || est.medidor?.[0]?.medidor || '';
  row[161] = cab.debito_automatico ? 'SIM' : '';

  // ── transbordo dinâmico: SCEE #3+ e injeção #3+ (nada se perde) ──
  let oc = BASE_HEADERS.length;
  for (let i = 2; i < scee.length; i++) {
    row[oc++] = scee[i].uc_geradora || ''; row[oc++] = parseBrNum(scee[i].geracao_ciclo_kwh);
    row[oc++] = scee[i].ciclo_mes || ''; row[oc++] = parseBrNum(scee[i].excedente_recebido_kwh);
  }
  for (let i = 2; i < injecoes.length; i++) {
    row[oc++] = injecoes[i].uc_geradora || ''; row[oc++] = injecoes[i].tipo_gd || ''; row[oc++] = parseBrNum(injecoes[i].qtd);
    row[oc++] = parseBrNum(getSafeTarifa(injecoes[i])); row[oc++] = parseBrNum(getSafePreco(injecoes[i])); row[oc++] = parseBrNum(injecoes[i].valor);
  }
  return row;
}

// injeções "principais" (mesmo filtro do builder) — usado para dimensionar overflow
export function countInjecoesPrincipais(est: FaturaEstruturada): number {
  return (est?.itens || []).filter((i) => {
    const d = normalizeStr(i.descricao || '');
    return d.includes('INJECAOSCEE') && !d.includes('TUSD') && !d.includes('TE') && !d.includes('FP') && !d.includes('HI') && !d.includes('HR');
  }).length;
}

// ─────────────────────── aba FATURAS (larga, 1 linha por fatura) ───────────────────────
const GROUP_INJ = 'Identificação da Unidade que injetou, quantidade, tarifa sem imposto, tarifa com imposto e valor';

export function buildFaturasGrid(faturas: ExcelFaturaItem[]): GridModel {
  const extraScee = Math.max(0, faturas.reduce((m, f) => Math.max(m, (f.estruturado?.scee?.length || 0)), 0) - 2);
  const extraInj = Math.max(0, faturas.reduce((m, f) => Math.max(m, countInjecoesPrincipais(f.estruturado)), 0) - 2);
  const overflowHeaders: string[] = [];
  for (let i = 3; i <= 2 + extraScee; i++) overflowHeaders.push(`UC Geradora ${i}`, `GERAÇÃO CICLO KWH ${i}`, `CICLO (MÊS) ${i}`, `EXCEDENTE RECEBIDO KWH ${i}`);
  for (let i = 3; i <= 2 + extraInj; i++) overflowHeaders.push(`UC inj ${i}`, `Tipo inj ${i}`, `INJEÇÃO SCEE ${i}`, `Tarifa unit.(R$) ${i}`, `Preço c/ trib. ${i}`, `Valor(R$) ${i}`);
  const headers = [...BASE_HEADERS, ...overflowHeaders];
  const nCols = headers.length;

  const dataRows = faturas.map(f => buildFaturaRow(f, nCols));

  // group headers (linha 0) — replica os agrupamentos coloridos do template antigo
  const groupHeaders = new Array(nCols).fill('');
  groupHeaders[71] = GROUP_INJ;
  groupHeaders[77] = GROUP_INJ;
  groupHeaders[94] = 'Grupo A e Tarifa Branca';
  groupHeaders[109] = 'Tarifa Branca';
  groupHeaders[117] = 'Grupo A e Tarifa Branca';

  const yellowGroupCols = new Set<number>();
  for (let c = 94; c <= 108; c++) yellowGroupCols.add(c);
  for (let c = 117; c <= 148; c++) yellowGroupCols.add(c);
  const redGroupCols = new Set<number>();
  for (let c = 109; c <= 116; c++) redGroupCols.add(c);

  const DROP = [3, 4];
  const dateColumns = remapIndexSet(new Set([6, 7, 9, 10, 11]), DROP);
  const textColumns = remapIndexSet(new Set([0, 1, 2, 3, 4, 5, 14, 15, 20, 21, 22, 23, 24, 25, 26, 42, 46, 50, 72, 78, 151, 158, 159, 160, 161]), DROP);
  const sixDecColumns = remapIndexSet(new Set([
    52, 60, 64, 68, 74, 80, 86, 91, 96, 100, 104, 108, 110, 114, 118, 122, 126, 130, 134, 138, 142, 146,
    53, 61, 65, 69, 75, 81, 87, 92, 97, 101, 105, 109, 111, 115, 119, 123, 127, 131, 135, 139, 143, 147,
    153,
  ]), DROP);
  const yellowCols = remapIndexSet(yellowGroupCols, DROP);
  const redCols = remapIndexSet(redGroupCols, DROP);

  const headersD = dropColumns(headers, DROP);
  const groupHeadersD = dropColumns(groupHeaders, DROP);
  const rows = dataRows.map((r) => dropColumns(r, DROP));

  const columns: ColMeta[] = headersD.map((label, c) => ({
    label,
    type: textColumns.has(c) ? 'txt' : dateColumns.has(c) ? 'date' : sixDecColumns.has(c) ? 'dec6' : 'num',
    band: yellowCols.has(c) ? 'yellow' : redCols.has(c) ? 'red' : undefined,
  }));

  // Coluna 1 (pedido do usuário): motivo pelo qual a fatura está em Revisar/Falhou,
  // destacada em âmbar na tela e no Excel. Prepend DEPOIS de todo o remapeamento —
  // os índices hardcoded de buildFaturaRow continuam valendo.
  return {
    columns: [{ label: 'Motivo Revisão', type: 'txt' }, ...columns],
    groupHeaders: ['', ...groupHeadersD],
    rows: rows.map((r, i) => [motivoRevisao(faturas[i].reconciliacao), ...r]),
    highlightCol: 0,
    rowOwner: faturas.map((_, i) => ({ fatura: i, chave: '0' })),
  };
}

// ─────────────────────── aba RESUMO (projeção da aba Faturas) ───────────────────────
// Recorte de leitura rápida: o que se confere numa fatura sem varrer as ~160 colunas da
// aba Faturas. É uma PROJEÇÃO dela — não uma segunda leitura do PDF —, então a correção
// manual (salva ou ainda pendente na tela) já chega aplicada aqui, e o que se edita no
// Resumo é gravado lá, na coluna de origem.

/** Colunas de QUANTIDADE injetada na aba Faturas, separadas por natureza. */
interface ColunasInjecao { principais: number[]; hi: number[]; tusd: number[]; te: number[] }

function colunasInjecao(columns: ColMeta[]): ColunasInjecao {
  const out: ColunasInjecao = { principais: [], hi: [], tusd: [], te: [] };
  columns.forEach((c, i) => {
    const l = c.label;
    if (!l.startsWith('INJEÇÃO SCEE')) return;                   // tarifa/preço/valor têm outro rótulo
    if (/^INJEÇÃO SCEE( \d+)?$/.test(l)) out.principais.push(i);  // grupo B (+ overflow)
    else if (l === 'INJEÇÃO SCEE HI') out.hi.push(i);
    else if (/-\s*TUSD$/.test(l)) out.tusd.push(i);
    else if (/-\s*TE$/.test(l)) out.te.push(i);
  });
  return out;
}

/** kWh na fatura tem 2 casas: sem isto a soma de várias parcelas carrega a cauda binária
 *  (7520.990000000001) para a célula e para o .xlsx. */
const round2 = (n: number) => Math.round(n * 100) / 100;

const somaCols = (row: (string | number)[], cols: number[]): number =>
  cols.reduce((s, c) => {
    const v = row[c];
    const n = typeof v === 'number' ? v : Number(parseBrNum(v));
    return s + (isNaN(n) ? 0 : n);
  }, 0);

/** kWh injetados na fatura. Grupo B traz uma linha só ("INJEÇÃO SCEE"); grupo A e tarifa
 *  branca quebram a MESMA energia em TUSD e TE por posto — somar as duas contaria o
 *  dobro, então TE só entra quando não veio TUSD. */
function totalInjetado(row: (string | number)[], cols: ColunasInjecao): number | '' {
  const porPosto = somaCols(row, cols.tusd) || somaCols(row, cols.te);
  const total = round2(somaCols(row, cols.principais) + somaCols(row, cols.hi) + porPosto);
  return total || '';
}

// ─── de QUEM é a energia injetada ───
// A fatura traz a UC geradora ao lado de cada injeção do grupo B; o cadastro do rateio
// guarda a UC de cada usina da Vigor. Cruzando os dois dá para dizer quanto da injeção
// veio de usina nossa e quanto veio de geração de terceiro (cliente que já tinha usina,
// consórcio de outra empresa). O grupo A / tarifa branca NÃO traz UC por parcela — essas
// caem em "sem UC", e é isso que a coluna própria do Resumo mostra.

/** Réplica local de FinanceiroLayout.cleanUC (mesma convenção de `mesReferenciaRateio`):
 *  a fatura traz "000406019301238" e o cadastro da UG, "4.060.193.012-38". */
export const limparUcGeradora = (uc: unknown): string =>
  (typeof uc === 'string' || typeof uc === 'number' ? String(uc) : '').replace(/[^0-9]/g, '').replace(/^0+/, '');

export type OrigemInjecao = 'vigor' | 'outros' | 'semUc';

/** Uma parcela de injeção da fatura, já ligada às colunas que a geram na aba Faturas —
 *  é por `colQtd`/`colUc` que a correção feita no Resumo volta para lá. */
export interface ParcelaInjecao {
  rotulo: string;              // rótulo da coluna de quantidade ('INJEÇÃO SCEE 3', 'INJEÇÃO SCEE FP - TUSD'…)
  colQtd: number;              // coluna dos kWh na aba Faturas
  colUc: number | null;        // coluna da UC geradora (só o grupo B tem)
  uc: string;                  // UC geradora como está na fatura
  qtd: number;
  origem: OrigemInjecao;
  /** TE que espelha um TUSD é a MESMA energia: aparece na lista, mas não soma. */
  contaNoTotal: boolean;
}

/** Bloco do grupo B na aba Faturas: [UC, Tipo, INJEÇÃO SCEE, tarifa, preço, valor] — daí
 *  a UC ficar duas colunas antes da quantidade (no overflow, 'UC inj 3'). */
const RE_COL_UC_INJ = /^UC( inj \d+)?$/;

/** De quem é a energia daquela parcela: usina da Vigor, geração de terceiro, ou a fatura
 *  não disse (grupo A / tarifa branca não trazem UC por parcela). */
export const origemDaUc = (uc: string, ucsVigor?: Set<string> | null): OrigemInjecao => {
  const limpa = limparUcGeradora(uc);
  if (!limpa) return 'semUc';
  return ucsVigor?.has(limpa) ? 'vigor' : 'outros';
};

/** Parcelas de injeção de UMA linha da aba Faturas, na ordem das colunas. Parcela sem
 *  kWh e sem UC não existe (coluna do template que esta fatura não usa). */
export function parcelasInjecao(
  columns: ColMeta[],
  row: (string | number)[],
  ucsVigor?: Set<string> | null,
): ParcelaInjecao[] {
  const cols = colunasInjecao(columns);
  const temTusd = somaCols(row, cols.tusd) !== 0;
  const monta = (colQtd: number, natureza: keyof ColunasInjecao): ParcelaInjecao | null => {
    const bruto = row[colQtd];
    const n = typeof bruto === 'number' ? bruto : Number(parseBrNum(bruto));
    const qtd = isNaN(n) ? 0 : n;
    const colUc = natureza === 'principais' && RE_COL_UC_INJ.test(columns[colQtd - 2]?.label || '') ? colQtd - 2 : null;
    const uc = colUc !== null ? String(row[colUc] ?? '').trim() : '';
    if (!qtd && !uc) return null;
    return {
      rotulo: columns[colQtd].label,
      colQtd,
      colUc,
      uc,
      qtd,
      origem: origemDaUc(uc, ucsVigor),
      contaNoTotal: natureza !== 'te' || !temTusd,
    };
  };
  const out: ParcelaInjecao[] = [];
  for (const natureza of ['principais', 'hi', 'tusd', 'te'] as (keyof ColunasInjecao)[]) {
    for (const c of cols[natureza]) { const p = monta(c, natureza); if (p) out.push(p); }
  }
  return out.sort((a, b) => a.colQtd - b.colQtd);
}

/** kWh injetados por origem — só as parcelas que contam (ver `contaNoTotal`). */
export function totaisInjecao(parcelas: ParcelaInjecao[]): Record<OrigemInjecao, number> {
  const t: Record<OrigemInjecao, number> = { vigor: 0, outros: 0, semUc: 0 };
  for (const p of parcelas) if (p.contaNoTotal) t[p.origem] += p.qtd;
  return { vigor: round2(t.vigor), outros: round2(t.outros), semUc: round2(t.semUc) };
}

/** Coluna calculada do Resumo: não espelha uma coluna da aba Faturas, sai de uma conta
 *  sobre várias. `injecao` é o total (fallback de quando não se sabe quais UCs são da
 *  Vigor); as três por origem são o padrão. */
type CalcResumo = 'injecao' | 'inj_vigor' | 'inj_outros' | 'inj_semUc';

interface CampoResumo { rotulo: string; origem?: string; calc?: CalcResumo }

/** Campos do Resumo, na ordem pedida. `origem` é o RÓTULO na aba Faturas: casar por
 *  rótulo (nunca por índice) sobrevive ao overflow de SCEE/injeção, que desloca colunas. */
const RESUMO_CAMPOS: CampoResumo[] = [
  { rotulo: 'Unidade Consumidora', origem: 'Unidade Consumidora' },
  { rotulo: 'Nº do Medidor', origem: 'Nº do Medidor' },
  { rotulo: 'Nome/Razão Social', origem: 'Nome/Razão Social' },
  { rotulo: 'CPF/CNPJ', origem: 'CPF/CNPJ' },
  { rotulo: 'Mês/Ref', origem: 'Mês/Ref' },
  { rotulo: 'Data da Leitura Anterior', origem: 'Data da Leitura Anterior' },
  { rotulo: 'Data da Leitura Atual', origem: 'Data da Leitura Atual' },
  { rotulo: 'Próxima Leitura', origem: 'Próxima Leitura' },
  { rotulo: 'Apresentação', origem: 'Apresentação' },
  { rotulo: 'Consumo (kWh)', origem: 'Consumo (kWh)' },
  { rotulo: 'Injeção (kWh)', calc: 'injecao' },
  { rotulo: 'Saldo Acumulado (kWh)', origem: 'SALDO' },
  { rotulo: 'Vencimento', origem: 'Vencimento' },
];

/** Com o cadastro das usinas em mãos, a coluna única de injeção vira três — de quem é a
 *  energia é justamente o que se quer conferir. Sem ele, não dá para separar sem chutar:
 *  o Resumo fica com o total, como antes. */
const CAMPOS_INJECAO_POR_ORIGEM: CampoResumo[] = [
  { rotulo: 'Injeção Vigor (kWh)', calc: 'inj_vigor' },
  { rotulo: 'Injeção Outros (kWh)', calc: 'inj_outros' },
  { rotulo: 'Injeção sem UC (kWh)', calc: 'inj_semUc' },
];

const CALC_POR_ORIGEM: Record<OrigemInjecao, CalcResumo> = { vigor: 'inj_vigor', outros: 'inj_outros', semUc: 'inj_semUc' };

/** Projeção do Resumo com o caminho de volta: `origemCols[c]` é o índice, na aba
 *  Faturas, da coluna que a coluna `c` do Resumo espelha — `null` quando ela é calculada
 *  (as de injeção) ou não existe neste lote. É por ele que a edição feita no Resumo é
 *  gravada na aba Faturas, que continua sendo a fonte. As colunas de injeção têm o
 *  caminho de volta por LINHA: `parcelas[r]` diz de qual coluna saiu cada kWh. */
export type ResumoGrid = GridModel & {
  origemCols: (number | null)[];
  /** Coluna do Resumo → origem que ela soma (só as de injeção separada). */
  injecaoCols: Map<number, OrigemInjecao>;
  /** Parcelas de injeção por linha, na ordem das colunas da aba Faturas. */
  parcelas: ParcelaInjecao[][];
};

/** Monta a aba Resumo a partir da aba Faturas JÁ montada (e já editada, quando vem da
 *  tela). As linhas são as mesmas, na mesma ordem — uma por fatura. `ucsVigor` são as UCs
 *  geradoras das usinas da Vigor (já limpas por `limparUcGeradora`); sem elas a injeção
 *  volta a ser uma coluna só. */
export function buildResumoGrid(faturas: GridModel | null | undefined, ucsVigor?: Set<string> | null): ResumoGrid | null {
  if (!faturas || !faturas.rows.length) return null;
  const porRotulo = new Map<string, number>();
  faturas.columns.forEach((c, i) => { if (!porRotulo.has(c.label)) porRotulo.set(c.label, i); });
  const inj = colunasInjecao(faturas.columns);
  const separa = !!ucsVigor;

  const modelo = separa
    ? RESUMO_CAMPOS.flatMap(c => (c.calc === 'injecao' ? CAMPOS_INJECAO_POR_ORIGEM : [c]))
    : RESUMO_CAMPOS;
  const campos = modelo.map(campo => ({ campo, col: campo.origem ? porRotulo.get(campo.origem) : undefined }));
  const columns: ColMeta[] = campos.map(({ campo, col }) => ({
    label: campo.rotulo,
    type: campo.calc ? 'num' : (col !== undefined ? faturas.columns[col].type : 'txt'),
  }));

  const parcelas = faturas.rows.map(row => (separa ? parcelasInjecao(faturas.columns, row, ucsVigor) : []));
  const rows: (string | number)[][] = faturas.rows.map((row, r) => {
    const totais = separa ? totaisInjecao(parcelas[r]) : null;
    return campos.map(({ campo, col }) => {
      if (campo.calc === 'injecao') return totalInjetado(row, inj);
      if (campo.calc === 'inj_vigor') return totais!.vigor || '';
      if (campo.calc === 'inj_outros') return totais!.outros || '';
      if (campo.calc === 'inj_semUc') return totais!.semUc || '';
      return col !== undefined ? row[col] : '';
    });
  });

  const origemCols = campos.map(({ campo, col }) => (campo.calc || col === undefined ? null : col));
  const injecaoCols = new Map<number, OrigemInjecao>();
  campos.forEach(({ campo }, c) => {
    for (const [origem, calc] of Object.entries(CALC_POR_ORIGEM) as [OrigemInjecao, CalcResumo][]) {
      if (campo.calc === calc) injecaoCols.set(c, origem);
    }
  });
  return { columns, rows, origemCols, injecaoCols, parcelas };
}

/** Onde a edição de uma célula do Resumo é gravada na aba Faturas.
 *
 *  As colunas espelhadas têm destino fixo (`origemCols`). As de injeção são somatórios:
 *  só têm destino quando a fatura inteira tem UMA parcela e ela é daquela origem — com
 *  duas ou mais, o número digitado não teria em qual coluna cair. `null` = a célula não
 *  se edita direto (ver `abreComposicaoInjecao`). */
export function alvoNaAbaFaturas(resumo: ResumoGrid, r: number, c: number): number | null {
  const fixo = resumo.origemCols[c];
  if (fixo !== null) return fixo;
  const origem = resumo.injecaoCols.get(c);
  const parcelas = resumo.parcelas[r] || [];
  if (!origem || parcelas.length !== 1 || parcelas[0].origem !== origem) return null;
  return parcelas[0].colQtd;
}

/** Célula de injeção que abre a composição parcela a parcela: as que não se editam
 *  direto, desde que a fatura tenha alguma injeção. Vale também para a célula vazia de
 *  outra origem — é por ela que se troca a UC da única parcela da fatura. */
export function abreComposicaoInjecao(resumo: ResumoGrid, r: number, c: number): boolean {
  if (!resumo.injecaoCols.has(c)) return false;
  return (resumo.parcelas[r] || []).length > 0 && alvoNaAbaFaturas(resumo, r, c) === null;
}

// ─────────────────────── abas "simples" (long-format) ───────────────────────
// ─── âncora dos sub-registros (abas long-format) ───
// Um pedaço de texto do próprio registro, normalizado. Vazio nunca: registro sem nada
// identificável cai em '?', e o desempate por ordinal cuida do resto.
const slugAncora = (...partes: unknown[]) => {
  const s = partes
    .map(p => String(p ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, ' ').trim().toUpperCase().replace(/\s+/g, '-'))
    .filter(Boolean)
    .join('~')
    .slice(0, 90);
  return s || '?';
};

/** Desambigua âncoras repetidas DENTRO da mesma fatura: a 2ª "INJECAO-SCEE" idêntica
 *  vira "INJECAO-SCEE#2". Duas linhas realmente iguais ainda dependem da ordem entre
 *  si — mas aí não há nada no conteúdo que as distinga, e o ordinal é o melhor que dá. */
function ancorasUnicas(brutas: string[]): string[] {
  const vistas = new Map<string, number>();
  return brutas.map(a => {
    const n = (vistas.get(a) || 0) + 1;
    vistas.set(a, n);
    return n === 1 ? a : `${a}#${n}`;
  });
}

function simpleGrid(headers: string[], rows: (string | number)[][], numericCols: Set<number>, dec6Cols: Set<number> = new Set(), severityCol?: number, rowOwner?: RowOwner[]): GridModel {
  const columns: ColMeta[] = headers.map((label, c) => ({
    label,
    type: dec6Cols.has(c) ? 'dec6' : numericCols.has(c) ? 'num' : 'txt',
  }));
  return { columns, rows, severityCol, rowOwner };
}

export function buildItensGrid(faturas: ExcelFaturaItem[]): GridModel {
  const headers = ['Arquivo', 'UC', 'Mês/Ref', 'Status', 'Seção', 'Descrição', 'Posto', 'Unid.', 'Qtd', 'Tarifa unit.(R$)', 'Preço c/ trib.(R$)', 'Valor(R$)', 'UC Geradora', 'Tipo GD'];
  const rows: (string | number)[][] = [];
  const owners: RowOwner[] = [];
  for (const [fi, f] of faturas.entries()) {
    const c: any = f.estruturado?.cabecalho || {};
    const itens = f.estruturado?.itens || [];
    const ancoras = ancorasUnicas(itens.map(it => slugAncora(it.descricao, it.posto, it.unidade)));
    for (const [si, it] of itens.entries()) {
      owners.push({ fatura: fi, chave: ancoras[si] });
      rows.push([
        f.arquivo, c.unidade_consumidora || '', contaMesToStr(c.conta_mes || ''), f.reconciliacao?.status || '', it.grupo_secao || '', it.descricao || '',
        it.posto || '', it.unidade || '', parseBrNum(it.qtd), parseBrNum(it.tarifa_unit), parseBrNum(it.preco_unit_com_tributos),
        parseBrNum(it.valor), it.uc_geradora || '', it.tipo_gd || '',
      ]);
    }
  }
  return simpleGrid(headers, rows, new Set([8, 9, 10, 11]), new Set([9, 10]), undefined, owners);
}

export function buildMedidorGrid(faturas: ExcelFaturaItem[]): GridModel {
  const headers = ['Arquivo', 'UC', 'Medidor', 'Grandeza', 'Posto', 'Leitura Anterior', 'Leitura Atual', 'Constante', 'Consumo (kWh)'];
  const rows: (string | number)[][] = [];
  const owners: RowOwner[] = [];
  for (const [fi, f] of faturas.entries()) {
    const c: any = f.estruturado?.cabecalho || {};
    const linhas = f.estruturado?.medidor || [];
    const ancoras = ancorasUnicas(linhas.map(m => slugAncora(m.medidor, m.grandeza, m.posto)));
    for (const [si, m] of linhas.entries()) {
      owners.push({ fatura: fi, chave: ancoras[si] });
      rows.push([
        f.arquivo, c.unidade_consumidora || '', m.medidor || '', m.grandeza || '', m.posto || '',
        m.leitura_anterior || '', m.leitura_atual || '', parseBrNum(m.constante), parseBrNum(m.consumo),
      ]);
    }
  }
  return simpleGrid(headers, rows, new Set([7, 8]), new Set([7]), undefined, owners);
}

export function buildAuditoriaGrid(faturas: ExcelFaturaItem[]): GridModel {
  const headers = ['Arquivo', 'UC', 'Tipo', 'Categoria', 'Campo', 'Mensagem', 'Declarado', 'Esperado'];
  const rows: (string | number)[][] = [];
  const owners: RowOwner[] = [];
  for (const [fi, f] of faturas.entries()) {
    const uc = (f.estruturado?.cabecalho as any)?.unidade_consumidora || '';
    const checagens = f.reconciliacao?.checagens || [];
    const ancoras = ancorasUnicas(checagens.map(ch => slugAncora(ch.categoria, ch.campo)));
    for (const [si, ch] of checagens.entries()) {
      owners.push({ fatura: fi, chave: ancoras[si] });
      rows.push([f.arquivo, uc, ch.tipo, ch.categoria, ch.campo, ch.mensagem, ch.valor_declarado || '', ch.valor_esperado || '']);
    }
  }
  return simpleGrid(headers, rows, new Set(), new Set(), 2, owners);
}

export function buildErrosGrid(erros: ExcelErroItem[]): GridModel {
  return simpleGrid(['Arquivo', 'Erro'], erros.map(e => [e.arquivo, e.erro]), new Set());
}

// ─────────────────────── conjunto completo de abas ───────────────────────
export interface WorkbookGrids {
  faturas: GridModel | null;
  itens: GridModel | null;
  medidor: GridModel | null;
  auditoria: GridModel | null;
  erros: GridModel | null;
}

// ─────────────────────── camada de correções manuais ───────────────────────
/** Chave de uma célula dentro da camada de correções de UMA fatura:
 *  âncora do sub-registro (RowOwner.chave) + índice da coluna. */
export const chaveCorrecao = (ancora: string, col: number) => `${ancora}:${col}`;

/** Aplica, sobre o grid recém-montado, o que já foi corrigido à mão e salvo no banco.
 *  Mutação in-place da matriz (o grid acabou de ser criado aqui, ninguém mais o viu) e
 *  marca as células em `corrigidas` para a tela poder sinalizar.
 *  Duas travas contra a correção cair na célula errada: o RÓTULO da coluna tem de bater
 *  (coluna que mudou de posição não recebe valor de outra) e a linha é encontrada pela
 *  ÂNCORA de conteúdo do sub-registro (item reordenado num reprocessamento continua
 *  levando a sua correção junto). Quando nada casa, a correção é ignorada — perder a
 *  correção é sempre melhor que aplicá-la no lugar errado. */
function aplicarCorrecoes(grid: GridModel | null, faturas: ExcelFaturaItem[], aba: TabKey): GridModel | null {
  if (!grid || !grid.rowOwner) return grid;
  const corrigidas = new Set<string>();
  for (let r = 0; r < grid.rows.length; r++) {
    const owner = grid.rowOwner[r];
    const celulas = faturas[owner?.fatura]?.correcoes?.abas?.[aba];
    if (!celulas) continue;
    for (let c = 0; c < grid.columns.length; c++) {
      const corr = celulas[chaveCorrecao(owner.chave, c)];
      if (!corr || corr.label !== grid.columns[c].label) continue;
      grid.rows[r][c] = corr.valor;
      corrigidas.add(`${r}:${c}`);
    }
  }
  return corrigidas.size ? { ...grid, corrigidas } : grid;
}

/** Monta TODAS as abas a partir dos dados. A planilha na tela guarda cópias editáveis
 *  disto e reexporta via `writeWorkbook` (mesmo caminho de estilo do download normal).
 *  As correções manuais salvas entram aqui — logo valem tanto na tela quanto em
 *  qualquer .xlsx gerado a partir de itens que as tragam. */
export function buildAllGrids(faturas: ExcelFaturaItem[], erros: ExcelErroItem[] = []): WorkbookGrids {
  const itens = buildItensGrid(faturas);
  const medidor = buildMedidorGrid(faturas);
  const auditoria = buildAuditoriaGrid(faturas);
  return {
    faturas: faturas.length ? aplicarCorrecoes(buildFaturasGrid(faturas), faturas, 'faturas') : null,
    itens: itens.rows.length ? aplicarCorrecoes(itens, faturas, 'itens') : null,
    medidor: medidor.rows.length ? aplicarCorrecoes(medidor, faturas, 'medidor') : null,
    auditoria: auditoria.rows.length ? aplicarCorrecoes(auditoria, faturas, 'auditoria') : null,
    erros: erros.length ? buildErrosGrid(erros) : null,
  };
}

// ─────────────────────── correção → escalares do Histórico ───────────────────────
/** Colunas da aba Faturas que também existem como escalar denormalizado em
 *  `extracoes_faturas` (a tabela do Histórico lista e busca por eles). Corrigir o nome
 *  na planilha tem de corrigir o nome na lista — senão os dois lugares divergem.
 *  Casadas por RÓTULO, não por índice: o índice muda com o overflow de SCEE/injeção. */
const ESCALAR_POR_LABEL: Record<string, 'uc_codigo' | 'nome_cliente' | 'cpf_cnpj' | 'mes_referencia' | 'grupo' | 'total_pagar'> = {
  'Unidade Consumidora': 'uc_codigo',
  'Nome/Razão Social': 'nome_cliente',
  'CPF/CNPJ': 'cpf_cnpj',
  'Mês/Ref': 'mes_referencia',
  'Grupo': 'grupo',
  'Valor do Boleto': 'total_pagar',
};

export interface EscalaresExtracao {
  uc_codigo?: string; nome_cliente?: string; cpf_cnpj?: string;
  mes_referencia?: string; grupo?: string; total_pagar?: number | null;
}

/** Extrai da camada de correções de uma fatura os escalares que precisam acompanhar. */
export function escalaresDeCorrecoes(correcoes: CorrecoesPlanilha | null | undefined): EscalaresExtracao {
  const out: EscalaresExtracao = {};
  for (const corr of Object.values(correcoes?.abas?.faturas || {})) {
    const campo = ESCALAR_POR_LABEL[corr.label];
    if (!campo) continue;
    const txt = String(corr.valor ?? '').trim();
    if (campo === 'total_pagar') {
      const n = parseBrNum(corr.valor);
      out.total_pagar = typeof n === 'number' ? n : null;
    } else if (campo === 'mes_referencia') {
      const iso = contaMesToIso(txt);
      if (iso) out.mes_referencia = iso;
    } else {
      out[campo] = txt;
    }
  }
  return out;
}
